/**
 * Turn today's slate into Create's pick slips (specs/home-page AC-10, AC-11). Pure.
 *
 * A 1X2 pick becomes the game's match-winner slip (`prediction`, no marketId); every other market
 * becomes its own "other pick" slip, exactly the shapes `usePickSlips` builds by hand.
 */

import { MARKET_IDS } from '../../create/constants/matchDetails.constants';
import type { PickSlip } from '../../create/types/create.types';
import type { SlateMatchView, TodaySlateView } from '../types/home.types';

const SCHEDULED_STATUS = 'SCHEDULED';
/** Create's quick-pick confidence, the same value a tap on the deck records. */
const DEFAULT_CONFIDENCE = 2;

const RESULT_PREDICTION: Record<string, PickSlip['prediction']> = {
  home: 'home',
  '1': 'home',
  draw: 'draw',
  x: 'draw',
  away: 'away',
  '2': 'away',
};

/** Slate matches still worth sending: scheduled and not yet kicked off (AC-11). */
export function visibleSlateMatches(slate: TodaySlateView | null | undefined, nowMs: number): SlateMatchView[] {
  if (!slate) return [];
  return slate.matches.filter((m) => {
    if (m.status !== SCHEDULED_STATUS) return false;
    const kickoff = new Date(m.kickoff).getTime();
    return Number.isFinite(kickoff) && kickoff > nowMs;
  });
}

export function slateToPickSlips(matches: SlateMatchView[]): PickSlip[] {
  const slips: PickSlip[] = [];
  for (const match of matches) {
    const names = { homeTeam: match.home.name, awayTeam: match.away.name };
    for (const pick of match.picks) {
      if (pick.marketId === MARKET_IDS.MATCH_WINNER) {
        const prediction = RESULT_PREDICTION[pick.value.trim().toLowerCase()];
        if (!prediction) continue;
        slips.push({ gameId: match.matchId, prediction, confidence: DEFAULT_CONFIDENCE, ...names });
        continue;
      }
      slips.push({
        gameId: match.matchId,
        marketId: pick.marketId,
        marketName: pick.marketName,
        value: pick.value,
        odd: pick.odd,
        ...names,
      });
    }
  }
  return slips;
}

/** The date Create should open on: the earliest kickoff among the sent matches. */
export function slatePrefillDateIso(matches: SlateMatchView[]): string | null {
  let min = Infinity;
  for (const m of matches) {
    const t = new Date(m.kickoff).getTime();
    if (Number.isFinite(t) && t < min) min = t;
  }
  return min === Infinity ? null : new Date(min).toISOString();
}
